import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { JcUtils } from "../../utils/JcUtils";
import { JcButton, JcButtonProps } from "./JcButton";
import { JcIconType } from "../icon/JcIcon";
import { JcIconButton } from "./JcIconButton";
import { JcBuiltInIcon, isJcBuiltInIcon } from "../icon/JcBuiltInIcons";
import { JcPopup } from "../dialog/JcPopup";

export interface JcPopupButtonProps extends Omit<JcButtonProps, "onClick" | "selected" | "children"> {
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly trigger: JcBuiltInIcon | React.ReactNode;
  readonly popupContent: React.ReactNode;
  /** Renders as a small dropdown anchored to the trigger (e.g. an overflow menu) instead of the default centered modal dialog. */
  readonly anchored?: boolean;
}

export const JcPopupButton = (props: JcPopupButtonProps): React.ReactElement => {

  const { open, setOpen, popupContent, anchored, trigger, ...buttonProps } = JcUtils.otherProps(props);
  const commonProps = JcUtils.commonProps(props, ["jc-popup-button"]);
  const popupClassName = JcUtils.classNameString(commonProps.className?.split(" ").map(name => name + "-popup") ?? []);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const toggle = () => setOpen(!open);
  const close = () => setOpen(false);

  const sharedTriggerProps = { ...buttonProps, ...commonProps, ref: triggerRef, onClick: toggle };
  const triggerElement = isJcBuiltInIcon(trigger)
    ? <JcIconButton iconProps={{ type: JcIconType.BUILT_IN, icon: trigger }} {...sharedTriggerProps} />
    : <JcButton {...sharedTriggerProps}>{trigger}</JcButton>;

  return <>
    {triggerElement}
    {anchored
      ? <JcAnchoredDropdown open={open} onClose={close} anchorRef={triggerRef} className={popupClassName}>{popupContent}</JcAnchoredDropdown>
      : <JcPopup open={open} onClose={close}>{popupContent}</JcPopup>}
  </>;
};

interface JcAnchoredDropdownProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly anchorRef: React.RefObject<HTMLElement | null>;
  readonly className: string;
  readonly children: React.ReactNode;
}

/**
 * A small dropdown positioned near `anchorRef` (bottom-left by default), flipping
 * up/right if it would overflow the viewport and clamped to stay within it. Only
 * `JcActionMenu` needs this — everything else uses the default centered `JcPopup`.
 */
const JcAnchoredDropdown = ({ open, onClose, anchorRef, className, children }: JcAnchoredDropdownProps): React.ReactElement | null => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: "hidden" });

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const content = contentRef.current;
    if (!open || anchor == null || content == null) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();

    const overflowsRight = anchorRect.left + contentRect.width > window.innerWidth;
    const overflowsBottom = anchorRect.bottom + contentRect.height > window.innerHeight;

    // Flipping alone can place a menu wider than the trigger's edge offset past
    // the opposite viewport edge (e.g. right-aligned on a narrow screen) — clamp
    // it back inside, keeping the top-left corner visible when it can't fully fit.
    const margin = 8;
    const left = overflowsRight ? anchorRect.right - contentRect.width : anchorRect.left;
    const top = overflowsBottom ? anchorRect.top - contentRect.height : anchorRect.bottom;

    setStyle({
      top: Math.max(margin, Math.min(top, window.innerHeight - contentRect.height - margin)),
      left: Math.max(margin, Math.min(left, window.innerWidth - contentRect.width - margin)),
    });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || contentRef.current?.contains(target)) {
        return;
      }
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div ref={contentRef} className={"jc-popup-anchored " + className} style={style}>
      {children}
    </div>,
    document.body,
  );
};
